import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useMutation, ApolloError } from '@apollo/client'

import { MUTATION_CREATE_PROJECT } from '@libs/apollo/mutations'

import { PROJECTS } from '@libs/apollo/queries'

import { Project, ProjectCreateInput, MutationCreateProjectArgs, ProjectFileType, LocalFile } from '@types'

import { useAppState } from '../app/app-state.hook'

import { useCreateProjectFile } from './create-project-file.hook'

interface Data {
  createProject?: Project
}

export const useCreateProject = (): {
  createProject: (input: Omit<ProjectCreateInput, 'organizationId'>, photos: LocalFile[], plans: LocalFile[]) => void
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const { organization } = useAppState()

  const { createProjectFile } = useCreateProjectFile()

  const [createProject, { loading, error, data }] = useMutation<Data, MutationCreateProjectArgs>(
    MUTATION_CREATE_PROJECT,
    {
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        {
          query: PROJECTS,
          variables: {
            input: { organizationId: organization?.id },
          },
        },
      ],
    },
  )

  const navigate = useNavigate()
  const { i18n } = useTranslation()

  const redirectToProjectPath = (project: Project) => {
    const path = `/${i18n.language}/projects/${project.id}/view`
    navigate(path)
  }

  const _createProject = async (
    input: Omit<ProjectCreateInput, 'organizationId'>,
    photos: LocalFile[],
    _plans: LocalFile[],
  ) => {
    try {
      const result = await createProject({ variables: { input: { organizationId: organization!.id, ...input } } })

      if (result.data?.createProject) {
        const projectId = result.data?.createProject.id

        await Promise.all(
          photos.map(async (file) => {
            await createProjectFile(
              {
                projectId,
                type: ProjectFileType.TypePhoto,
                name: file.name,
              },
              file,
            )
          }),
        )

        redirectToProjectPath(result.data?.createProject)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return { createProject: _createProject, loading, error, data }
}
